﻿import {Core} from "../../Base/Core";
import {PlayerPrefs} from "../../Utils/Unity/PlayerPrefs";
import {UICommonPopup} from "../Tools/Base/UICommonPopup";
import {RewardType} from "../../enums";
import {UIText} from "../Tools/Base/UIText";
import {UINineSlice} from "../Tools/Base/UINineSlice";
import {UISprite} from "../Tools/Base/UISprite";
import {RewardedAdsBtn} from "../RewardedAdsBtn";

const PREFS_FREE_CHEST = "ruy3n874xz3948rymx34r";

class ChestRewardsItem extends UISprite {
    static createList() {
        let types = new Set();
        for (let reward of Core.GameData.Chest.rewards) {
            types.add(reward.type);
        }
        return [...types].map((type) => new this(type)).filter((item) => item.Active);
    }

    constructor(rewardType) {
        super();
        this.rewardType = rewardType;
        this.Init();
    }

    get rewards() {
        return Core.GameData.Chest.rewards.filter((reward) => reward.type === this.rewardType);
    }

    get values() {
        return this.rewards.map((reward) => reward.value || reward.valueInt);
    }

    get iconImage() {
        switch (this.rewardType) {
            case RewardType.MoneyHard:
                return "ico-diamond";
            case RewardType.MoneySoftBySoftSpeed:
                return "ico-gold";
            case RewardType.RandomCreatureFromCurrentEpoch:
                return "box";
            case RewardType.BoosterIncomeX4:
                return "x4";
            case RewardType.BoosterCreaturesDiscount:
                return "upgrades/booster-monstersale-ico";
        }
        return "";
    }

    get labelText() {
        let values = this.values;
        let range = [Infinity, -Infinity];
        if (values.length > 1) {
            for (let value of values) {
                if (value < range[0]) range[0] = value;
                if (value > range[1]) range[1] = value;
            }
        }

        switch (this.rewardType) {
            case RewardType.MoneyHard:
                return range.join("-");
            case RewardType.MoneySoftBySoftSpeed:
                return range.map((val) => NUtils.FormatMoney(val)).join("-");
            case RewardType.RandomCreatureFromCurrentEpoch:
                return Core.Localizator.Get("random_monster");
            case RewardType.BoosterIncomeX4:
                return range.map((s) => Math.ceil(s / 60)).join("-") + " " + Core.Localizator.Get("minutes");
            case RewardType.BoosterCreaturesDiscount:
                return range.map((s) => Math.ceil(s / 60)).join("-") + " " + Core.Localizator.Get("minutes");
        }
        return "";
    }

    Awake() {
        super.Awake();
        this.icon = this.addChild(new UISprite());
        this.label = this.addChild(new UIText(""));
    }

    Init() {
        super.Init();
        this.icon.setImage(this.iconImage);
        this.label.text = this.labelText;
        this.SetActive(this.rewards.length > 0);
    }

    fitLayout(parentRect) {
        this.position.copyFrom(parentRect);
        let rect = parentRect.clone();
        rect.x = rect.y = 0;

        let iconRect = rect.clone().pad(-rect.width * 0.15, -rect.height * 0.15);
        iconRect.x = (rect.width - iconRect.width) * 0.5;
        iconRect.y = (rect.height - iconRect.height) * 0.2;
        this.icon.fitRect(iconRect);

        let labelRect = rect.clone();
        labelRect.height *= 0.25;
        labelRect.y = rect.height - labelRect.height;
        this.label.fitLayout(labelRect);
    }
}

class ChestRewards extends UISprite {
    Awake() {
        super.Awake();

        this.back = this.addChild(new UINineSlice("bg-simple"));
        this.back.tint = 0x000000;
        this.back.alpha = 0.3;
        this.items = [];
    }

    Init() {
        this.items = ChestRewardsItem.createList();
        for (let item of this.items) {
            this.addChild(item);
        }
    }

    fitLayout(parentRect) {
        this.position.copyFrom(parentRect);

        let rect = parentRect.clone();
        rect.x = rect.y = 0;

        this.back.fitLayout(rect);

        // Hardcoded 2 rows, 3 and 2 rewards
        let iconSize = Math.min((rect.width * 0.8) / 3, (rect.height * 0.9) / 2);
        let paddingX = (rect.width - iconSize * 3) / 4;
        let paddingY = (rect.height - iconSize * 2) / 3;
        let iconRect = new PIXI.Rectangle(paddingX, paddingY, iconSize, iconSize);
        for (let i = 0; i < this.items.length; i++) {
            this.items[i].fitLayout(iconRect);
            if (i === 2) {
                // Shift the row
                iconRect.x = (rect.width - (iconSize * 2 + paddingX)) * 0.5;
                iconRect.y = iconSize + paddingY * 2;
            } else {
                // Shift the X position
                iconRect.x += iconSize + paddingX;
            }
        }
    }
}

export class UIChestOpen extends UICommonPopup {
    get freeChestOpened() {
        return PlayerPrefs.HasKey(PREFS_FREE_CHEST);
    }

    set freeChestOpened(value) {
        PlayerPrefs.SetInt(PREFS_FREE_CHEST, 1);
    }

    Awake() {
        super.Awake({
            title: Core.Localizator.Get("chest_open_title"),
            closeButton: true,
            sound: "CreatureMerge"
        });

        this.onWatched = this.onWatched.bind(this);

        this.description = this.content.addChild(new UIText(Core.Localizator.Get("chest_open_disc")));
        this.description.fill = 0xc2c2c2;
        this.rewardItems = this.content.addChild(new ChestRewards());

        this.openBtn = this.content.addChild(
            new RewardedAdsBtn("btn-green", {
                label: Core.Localizator.Get("chest_open_button"),
                icon: this.freeChestOpened ? "play-icon" : undefined,
                iconAlign: "right",
                iconSize: 0.7,
            })
        );

        this.openBtn.on("pointertap", () => {
            if (this.freeChestOpened) {
                Core.Ads.TriggerRewarded(this.onWatched);
            } else {
                this.Hide();
                Core.UI.chestReward.Show();
                this.freeChestOpened = true;
                Core.Analytics.SendChestEvent("free_chest");
            }
        });

        this.closeBtn.on("pointertap", () => {
            Core.UI.game.chestBtn.SetActive(false);
            Core.UI.game.chestBtn.ShowAfterNMinutes(30);
            this.Hide();
        });
    }

    Init() {
        this.rewardItems.Init();
    }

    onWatched() {
        this.Hide();
        Core.UI.chestReward.Show();
        Core.Analytics.SendChestEvent("video_ads");
    }

    fitLayout(viewport = Core.app.viewport) {
        let sideRatio = 1.1;
        let w = Math.min(viewport.width * 0.8, (viewport.height * 0.6) / sideRatio);
        let h = w * sideRatio;

        this.setPopupSize(w, h);

        super.fitLayout(viewport);

        let contentRect = this.getContentSize();

        let rect = contentRect.clone();
        rect.height = contentRect.height * 0.07;
        rect.width = contentRect.width * 0.8;
        rect.x = (contentRect.width - rect.width) * 0.5;
        rect.y = (contentRect.height - rect.height) * 0.21;
        this.description.fitLayout(rect);

        rect = contentRect.clone();
        rect.height = contentRect.height * 0.45;
        rect.width = contentRect.width * 0.8;
        rect.x = (contentRect.width - rect.width) * 0.5;
        rect.y = (contentRect.height - rect.height) * 0.56;
        this.rewardItems.fitLayout(rect);

        rect = contentRect.clone();
        rect.height = contentRect.height * 0.15;
        rect.width = contentRect.width * 0.5;
        rect.x = (contentRect.width - rect.width) * 0.5;
        rect.y = (contentRect.height - rect.height) * 0.95;
        this.openBtn.fitLayout(rect);
    }
}
